import { AiIcon } from '@/assets/icons/ai-icon';
import { VoiceRecording } from '@/components/common/voice-recording';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';
import { $toggleLink } from '@lexical/link';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import type { SerializedEditorState } from 'lexical';
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import type { LucideIcon } from 'lucide-react';
import { Bold, Italic, Link, List, ListOrdered, MoreVertical, Strikethrough, Underline } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PulseLoader } from 'react-spinners';
import { isAIWritingEnabledForEntity } from '../config/ai-writing-config';
import type { AIActionType } from '../hooks/use-improve-writing';
import { useImproveWriting } from '../hooks/use-improve-writing';
import type { EntityType } from '../types/entity-types';
import ImproveWritingButton from './improve-writing-button';
import { ImproveWritingPreview } from './improve-writing-preview';

function Divider({ className }: { className?: string }): React.ReactNode {
    return <div className={cn('w-px min-w-px h-4 bg-neutral-300 mx-1 shrink-0', className)} />;
}

type ToolbarItem = {
    key: string;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
    selected?: boolean;
    alwaysVisible?: boolean;
    showDividerBefore?: boolean;
};

// AI writing improvement workflow states
type AIWritingState = 'idle' | 'improve-loading' | 'improve-preview' | 'improve-error';

type Props = {
    disableVoiceRecording?: boolean;
    entityType?: EntityType;
    entityId?: string;
    aiWritingState: AIWritingState;
    setAIWritingState: (state: AIWritingState) => void;
    onChange?: (value: SerializedEditorState) => void;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
};

export function ToolbarPlugin({
    disableVoiceRecording = false,
    entityType,
    entityId,
    aiWritingState,
    setAIWritingState,
    onChange,
    onAIInteraction,
}: Props) {
    const { enableVoiceRecording, enableAIWriting } = useAppConfig();
    const { t } = useLocale();
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    // These states are registered with editor commands; keep them for future use.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_canUndo, setCanUndo] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_canRedo, setCanRedo] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);

    // Use the improve writing hook to manage preview/original text state
    const { acceptPreview, rejectPreview, improveWriting, cancelImproveWriting, rateFeedback, hasRated } =
        useImproveWriting({
            entityType,
            entityId,
            editor,
            setAIWritingState,
            onChange,
            onAIInteraction,
        });

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
        }
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor, $updateToolbar]);

    const insertLink = useCallback(() => {
        const url = prompt(t('rich_text_editor__enter_url'));
        if (url) {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $toggleLink(url, {
                        target: '_blank',
                    });
                }
            });
        }
    }, [editor, t]);

    const showAIWritingButton = enableAIWriting && isAIWritingEnabledForEntity(entityType);

    const toolbarItems: ToolbarItem[] = useMemo(
        () => [
            {
                key: 'bold',
                icon: Bold,
                label: t('rich_text_editor__format_bold'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
                selected: isBold,
                alwaysVisible: true,
            },
            {
                key: 'italic',
                icon: Italic,
                label: t('rich_text_editor__format_italic'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
                selected: isItalic,
                alwaysVisible: true,
            },
            {
                key: 'underline',
                icon: Underline,
                label: t('rich_text_editor__format_underline'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'),
                selected: isUnderline,
                alwaysVisible: !showAIWritingButton,
            },
            {
                key: 'strikethrough',
                icon: Strikethrough,
                label: t('rich_text_editor__format_strikethrough'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough'),
                selected: isStrikethrough,
                alwaysVisible: !showAIWritingButton,
            },
            {
                key: 'numbered-list',
                icon: ListOrdered,
                label: t('rich_text_editor__insert_numbered_list'),
                onClick: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
                showDividerBefore: true,
                alwaysVisible: !showAIWritingButton,
            },
            {
                key: 'bullet-list',
                icon: List,
                label: t('rich_text_editor__insert_bullet_list'),
                onClick: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
            },
            {
                key: 'link',
                icon: Link,
                label: t('rich_text_editor__insert_link'),
                onClick: insertLink,
                showDividerBefore: true,
            },
        ],
        [t, editor, isBold, isItalic, isUnderline, isStrikethrough, insertLink, showAIWritingButton],
    );

    return (
        <div className="flex flex-col relative pt-2">
            {aiWritingState === 'improve-loading' && (
                <div className="flex flex-row items-center justify-between">
                    <div className="flex gap-2 items-center ">
                        <AiIcon />
                        <div className="flex items-end">
                            <Text variant="ai" className="leading-none">
                                {t('rich_text_editor__improve_writing_loading')}
                            </Text>
                            <PulseLoader size={3} color="#8D54E9" />
                        </div>
                    </div>
                    <Button variant="subtle-ai" onClick={cancelImproveWriting}>
                        {t('form__cancel')}
                    </Button>
                </div>
            )}

            {aiWritingState === 'improve-preview' && (
                <ImproveWritingPreview
                    onAccept={acceptPreview}
                    onReject={rejectPreview}
                    onRate={rateFeedback}
                    improveWriting={improveWriting}
                    entityType={entityType}
                    hasRated={hasRated}
                />
            )}

            <div
                className={cn('flex-row justify-between items-center hidden', {
                    // hide toolbar while improving or when preview is active
                    flex: aiWritingState === 'idle',
                })}
            >
                <div className="flex items-center gap-1 flex-1" ref={toolbarRef}>
                    {/* Toolbar buttons */}
                    {toolbarItems.map((item) => (
                        <span key={item.key} className="contents">
                            {item.showDividerBefore && (
                                <Divider
                                    className={cn('sm:block', {
                                        hidden: !item.alwaysVisible,
                                    })}
                                />
                            )}
                            <Tooltip>
                                <TooltipTrigger className={item.alwaysVisible ? undefined : 'hidden sm:flex'}>
                                    <Button
                                        variant="ghost"
                                        size="icon-s"
                                        className="hover:bg-transparent shrink-0"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            item.onClick();
                                        }}
                                        selected={item.selected}
                                        aria-label={item.label}
                                    >
                                        <item.icon className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{item.label}</TooltipContent>
                            </Tooltip>
                        </span>
                    ))}

                    <Divider className="sm:hidden" />

                    {/* Overflow dropdown - visible only on mobile */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-s"
                                className="hover:bg-transparent shrink-0 sm:hidden"
                                aria-label={t('rich_text_editor__more_options')}
                            >
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {toolbarItems.map((item) => (
                                <DropdownMenuItem
                                    key={item.key}
                                    onSelect={item.onClick}
                                    selected={item.selected}
                                >
                                    <item.icon className="h-4 w-4" />
                                    <Text size="sm">{item.label}</Text>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="flex items-center gap-2">
                    {showAIWritingButton && (
                        <span
                            className={cn({
                                'max-sm:hidden': isVoiceRecording,
                            })}
                        >
                            <ImproveWritingButton improveWriting={improveWriting} />
                        </span>
                    )}

                    {enableVoiceRecording && !disableVoiceRecording && (
                        <VoiceRecording
                            onRecordingStateChange={setIsVoiceRecording}
                            onInput={(text) => {
                                editor.update(() => {
                                    const root = $getRoot();

                                    if (!root.getTextContent()) {
                                        root.clear();
                                    }

                                    const paragraphNode = $createParagraphNode();

                                    const textNode = $createTextNode(text);

                                    paragraphNode.append(textNode);

                                    root.append(paragraphNode);
                                });
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
